import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Typography,
  Tooltip,
} from "@mui/material";
import { TableFooterPagination } from "../TableFooterPagination";
import { usersTableColumns } from "../users-helpers";
import { BasicUser, UsersQueryParams } from "@store/users.store";
import { CopyableText } from "../components/CopyableText";
import { TableBox, TableText } from "@shared/components/Table";
import styled from "@emotion/styled";
import { UsersListActionsMenu } from "./UsersListActionsMenu";

type Props = {
  users: BasicUser[];
  isLoadingUsers: boolean;
  usersQueryParams: UsersQueryParams;
  isFetchingNextPage: boolean;
  isLastPage: boolean;
  handleNextPage: () => void;
  handlePreviousPage: () => void;
  handleViewProfileClick: (id: string) => void;
  handleEditUserClick: (id: string) => void;
  handleUserLogsClick: (id: string) => void;
  handleUserSubscriptionClick: (id: string) => void;
  handleResetPasswordClick: (id: string) => void;
  handleImpersonateClick: (id: string) => void;
  isResettingPassword: boolean;
  isImpersonating: boolean;
};

function shortenUUID(uuid: string): string {
  if (!uuid || uuid.length < 8) return uuid;
  return `${uuid.slice(0, 4)}...${uuid.slice(-4)}`;
}

const ShortUUID = styled(CopyableText)`
  font-family: "Courier New", Courier, monospace;
`;

export const UsersList = (props: Props) => {
  const {
    users,
    isLoadingUsers,
    usersQueryParams,
    isFetchingNextPage,
    isLastPage,
    handleNextPage,
    handlePreviousPage,
    handleViewProfileClick,
    handleEditUserClick,
    handleUserLogsClick,
    handleUserSubscriptionClick,
    handleResetPasswordClick,
    handleImpersonateClick,
    isResettingPassword,
    isImpersonating,
  } = props;

  return (
    <TableBox>
      <TableContainer>
        <Table aria-label="table">
          <TableHead>
            <TableRow>
              {usersTableColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingUsers &&
              users.length === 0 &&
              Array(10)
                .fill(null)
                .map((_, index) => (
                  <TableRow hover tabIndex={-1} key={index}>
                    <TableCell colSpan={usersTableColumns.length}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={48}
                      />
                    </TableCell>
                  </TableRow>
                ))}

            {!isLoadingUsers && users.length === 0 && (
              <TableRow hover>
                <TableCell colSpan={usersTableColumns.length} align="center">
                  <Typography variant="body2">No users found</Typography>
                </TableCell>
              </TableRow>
            )}

            {!isLoadingUsers &&
              users.map((user) => {
                return (
                  <TableRow hover tabIndex={-1} key={user.id}>
                    <TableCell>
                      <Tooltip title={user.trackingId}>
                        <span>
                          <ShortUUID text={user.trackingId}>
                            {shortenUUID(user.trackingId)}
                          </ShortUUID>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {user.name && user.name !== "Unknown" ? (
                        <TableText>{user.name}</TableText>
                      ) : (
                        <TableText variant="body2">Unknown</TableText>
                      )}
                    </TableCell>
                    <TableCell>
                      <TableText>{user.email}</TableText>
                    </TableCell>

                    {/* <TableCell align="center">
                      <IconButton
                        onClick={() => handleEditUser(user.id)}
                        type="button"
                      >
                        <PencilIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleUserLogsClick(user.id)}
                        type="button"
                      >
                        <MoreHorizIcon />
                      </IconButton>
                    </TableCell> */}

                    <TableCell align="center">
                      <UsersListActionsMenu
                        onViewProfileClick={() =>
                          handleViewProfileClick(user.id)
                        }
                        onEditUserClick={() => handleEditUserClick(user.id)}
                        onUserLogsClick={() => handleUserLogsClick(user.id)}
                        onUserSubscriptionClick={() =>
                          handleUserSubscriptionClick(user.id)
                        }
                        onResetPasswordClick={() =>
                          handleResetPasswordClick(user.id)
                        }
                        onImpersonateClick={() =>
                          handleImpersonateClick(user.id)
                        }
                        isResettingPassword={isResettingPassword}
                        isImpersonating={isImpersonating}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
          <TableFooterPagination
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
            page={usersQueryParams.page}
            isFetchingNextPage={isFetchingNextPage}
            isLastPage={isLastPage}
          />
        </Table>
      </TableContainer>
    </TableBox>
  );
};
